import React, { useEffect, useRef, ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

// Helper function to get modal size class - moved outside component to prevent recreation
const getModalSizeClass = (className: string): string => {
    if (className.includes('modal-sm')) return 'max-w-[30rem]';
    if (className.includes('modal-md')) return 'max-w-[40rem]';
    if (className.includes('modal-lg')) return 'max-w-[60rem]';
    if (className.includes('modal-xl')) return 'max-w-[80rem]';
    if (className.includes('modal-full')) return 'max-w-[95vw] max-h-[95vh]';
    return '';
};

interface BaseModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    icon?: ReactNode;
    iconColor?: string;
    children: ReactNode;
    footer?: ReactNode;
    width?: string;
    closeOnBackdrop?: boolean;
    closeOnEscape?: boolean;
    showCloseButton?: boolean;
    className?: string;
    /** Show danger-colored accent bar instead of primary */
    danger?: boolean;
}

export const BaseModal: React.FC<BaseModalProps> = ({
    isOpen,
    onClose,
    title,
    icon,
    iconColor = "#3B6FEF",
    children,
    footer,
    width = "40rem",
    closeOnBackdrop = true,
    closeOnEscape = true,
    showCloseButton = true,
    className = "",
    danger = false,
}) => {
    const modalRef = useRef<HTMLDivElement>(null);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            const originalOverflow = document.body.style.overflow;
            const originalPaddingRight = document.body.style.paddingRight;
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollbarWidth}px`;

            return () => {
                document.body.style.overflow = originalOverflow;
                document.body.style.paddingRight = originalPaddingRight;
            };
        }
    }, [isOpen]);

    // Handle escape key
    useEffect(() => {
        if (!isOpen || !closeOnEscape) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, closeOnEscape, onClose]);

    // Handle backdrop click
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (closeOnBackdrop && e.target === e.currentTarget) {
            onClose();
        }
    };

    // Focus trap
    useEffect(() => {
        if (!isOpen || !modalRef.current) return;

        const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        const handleTab = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement?.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement?.focus();
                }
            }
        };

        document.addEventListener('keydown', handleTab);
        setTimeout(() => firstElement?.focus(), 100);

        return () => document.removeEventListener('keydown', handleTab);
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const accentGradient = danger
        ? "from-danger-500 via-danger-600 to-danger-700"
        : "from-primary-500 via-primary-600 to-primary-700";

    const modalContent = (
        <div
            className="fixed inset-0 w-screen h-screen z-[99999] p-0 md:p-4 overflow-y-auto m-0 animate-fade-in"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
        >
            {/* Enhanced gradient backdrop with blur */}
            <div className="fixed inset-0 bg-gradient-to-br from-neutral-900/80 via-neutral-800/75 to-neutral-900/80 dark:from-black/90 dark:via-black/85 dark:to-black/90 backdrop-blur-md" />

            {/* Center container */}
            <div className="flex items-end md:items-center justify-center min-h-full">
                <div
                    className={`relative bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-full md:max-w-[90vw] max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col animate-scale-in border border-neutral-200/50 dark:border-neutral-800/50 mx-auto z-[100000] ${getModalSizeClass(className)} ${className}`}
                    ref={modalRef}
                    style={{ maxWidth: width }}
                >
                    {/* Decorative gradient accent bar */}
                    <div className={`h-1 bg-gradient-to-r ${accentGradient}`} />

                    {/* Header */}
                    <div className="py-4 px-4 md:py-5 md:px-6 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between bg-transparent relative shrink-0 select-none">
                        <div className="flex items-center gap-3">
                            {icon && (
                                <span className="text-2xl shrink-0" style={{ color: iconColor }}>
                                    {icon}
                                </span>
                            )}
                            <h5 className="m-0 text-lg font-semibold text-neutral-900 dark:text-neutral-100 leading-tight" id="modal-title">
                                {title}
                            </h5>
                        </div>
                        <div className="flex items-center gap-2">
                            {showCloseButton && (
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-300 transition-all duration-200"
                                    onClick={onClose}
                                    aria-label="Close"
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Body */}
                    <div className="p-4 md:p-6 overflow-y-auto overflow-x-hidden flex-1 scrollbar-thin">
                        {children}
                    </div>

                    {/* Footer */}
                    {footer && (
                        <div className="py-4 px-4 md:px-6 border-t border-neutral-200 dark:border-neutral-800 flex flex-col-reverse md:flex-row justify-end gap-3 bg-neutral-50 dark:bg-neutral-900/50 shrink-0 rounded-b-2xl [&>*]:w-full md:[&>*]:w-auto">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};
